import { PagingType } from '@components/common/UTable/UTable';

import {
  Dictionaries,
  Dictionary,
  DictionaryItem,
  TransformedDictionaries,
} from '@/api/dictionaries/types';
import { GroupedPermission, PermissionResponse } from '@/api/roles/types';
import { MODULE_TYPE } from '@/shared/roles/enums';

export const mapTableIndexWithPage = (data?: any[], paging?: PagingType) => {
  return data
    ? data.map((record: object, index: number) => ({
        ...record,
        index: index + 1 + (paging ? paging.page * paging.size : 0),
      }))
    : [];
};

export const mapTableIndex = <T>(data: T[]): (T & { index: number })[] => {
  if (!data) return [];

  return data.map((record, index) => ({
    ...record,
    index: index + 1,
  }));
};

export const generateSimpleKey = () => `${Date.now() + Math.random().toString(36)}`;

export const enumToOptions = <T extends Record<string, string>>(en?: T) => {
  return en
    ? Object.entries(en).map(([key, value]) => ({
        label: value,
        value: key,
      }))
    : [];
};

export const enumValuesToOptions = <T extends Record<string, string>>(en?: T) => {
  return en
    ? Object.values(en).map((value) => ({
        label: value,
        value: value,
      }))
    : [];
};

export const dictToOptions = <T extends DictionaryItem[]>(dict?: T) => {
  return dict
    ? dict.map(({ id, name }) => ({
        label: name,
        value: id,
      }))
    : [];
};

export const arrayToOptions = <T extends string[]>(arr?: T) => {
  return arr ? arr.map((value) => ({ label: value, value: value })) : [];
};

export const downloadBlob = (data: Blob, filename: string) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export function transformDictionariesToMap(data: Dictionaries): TransformedDictionaries {
  return Object.fromEntries(
    (Object.keys(Dictionary) as Array<keyof typeof Dictionary>).map((key) => [
      key,
      Object.fromEntries((data[key] || []).map((item) => [item.id, item])),
    ]),
  ) as TransformedDictionaries;
}

export const groupByModule = (permissions: PermissionResponse[]): GroupedPermission[] => {
  if (!permissions) return [];
  const map = new Map<string | null, PermissionResponse[]>();

  for (const p of permissions) {
    const key = p?.module ?? null;
    const arr = map.get(key) ?? [];
    arr.push(p);
    map.set(key, arr);
  }

  const result: GroupedPermission[] = [];
  for (const [module, options] of map.entries()) {
    result.push({ module: MODULE_TYPE[module as keyof typeof MODULE_TYPE], options });
  }
  return result;
};
export const removeEmptyChildren = (data: any, propName: string = 'children') => {
  return data?.map((item: any) => {
    if (item[propName] && item[propName].length === 0) {
      const { [propName]: unusedValue, ...rest } = item;
      return rest;
    }
    if (item[propName] && item[propName].length > 0) {
      return { ...item, [propName]: removeEmptyChildren(item[propName], propName) };
    }
    return item;
  });
};

export const processFacilities = (items: any[]) => {
  return items?.map((item: any) => {
    const result = { ...item };

    if (result.facilities?.length) {
      result.facilities = result.facilities.map((facility: any) => ({
        ...facility,
        parentId: item.id,
        level: 'FACILITY',
      }));
    } else {
      delete result.facilities;
    }

    return result;
  });
};

export const mapSortToQueryParameter = (sorter?: any[]) => {
  return sorter?.filter((s) => s?.key && s?.order)?.map((s) => `${s.key},${s.order}`);
};
